﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../../utilities/crud-functions';
import DocumentLoader from '../DocumentLoader';
import { RawMaterialBatch } from '../../models/raw-material-batch';
import { Vendor } from '../../models/vendor';
import { ToastProps } from '../../models/toast-props';
import { LookupItem } from '../../models/lookup-item';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { DocumentType, ModalMessageType } from '../../models/enums';

import DataGrid, {
    Column,
    Selection,
    Toolbar,
    Popup,
    Form,
    Lookup,
    Item as ToolbarItem,
    Button as ColumnButton,
    Editing,
    Paging,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Item } from 'devextreme-react/form';
import Toast from 'devextreme-react/toast';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

let key = 1;

export interface Props {
    productId: string;
    vendorPartNumber?: string;
    vendorPartName?: string;
    isReadOnly: boolean;
}

export default function ProductRawMaterialsData(props: Props) {
    const [rawMaterialBatches, setRawMaterialBatches] =
        useState<RawMaterialBatch[]>();

    const [selectedRawMaterialBatch, setSelectedRawMaterialBatch] =
        useState<RawMaterialBatch>();

    const [vendors, setVendors] =
        useState<Vendor[]>();

    const [countries, setCountries] =
        useState<LookupItem[]>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.productId]);

    const title = () => {
        if (!props.vendorPartNumber && !props.vendorPartName)
            return "Raw Material Batches";

        let gridTitle = "";

        if (props.vendorPartNumber)
            gridTitle = props.vendorPartNumber;

        if (props.vendorPartName)
            gridTitle += ` (${props.vendorPartName})`;

        return `${gridTitle} Batches`;
    }

    return (
        <div>
            <div>
                <DataGrid id="ProductionRawMaterialsDataGrid"
                    height="16rem"
                    wordWrapEnabled={true}
                    dataSource={rawMaterialBatches ? rawMaterialBatches : []}
                    rowAlternationEnabled={true}
                    showBorders={true}
                    allowColumnResizing={true}
                    onSelectionChanged={onRawMaterialBatchSelectionChanged}
                    onRowValidating={onRawMaterialBatchRowValidating}
                    onRowRemoving={onRawMaterialBatchRowRemoving}
                    showRowLines={true}
                    keyExpr="rawMaterialBatchId"
                    columnResizingMode='nextColumn'>

                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column type="buttons" width="7rem">
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onMethodAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onMethodInformationButtonClicked} />
                    </Column>

                    <Column dataField='batchName' dataType="string" width='10rem'
                        cssClass="table-vertical-align" />

                    <Column dataField='vendorId' caption="Vendor" dataType="string" width='10rem' >
                        <Lookup dataSource={vendors}
                            allowClearing={true} valueExpr='vendorId' displayExpr='name' />
                    </Column>

                    <Column dataField='vendorPartNumber' caption="Part#" dataType="string" width='10rem'
                        cssClass="table-vertical-align" />

                    <Column dataField='vendorPartName' caption="Part Name" dataType="string" width='10rem'
                        cssClass="table-vertical-align" />

                    <Column dataField='purity' dataType="number"
                        format={decimalFormat} width='8rem' cssClass="table-vertical-align" />
                    <Column dataField='stockConcentrationPercentPpm' dataType="number" caption="Stock Conc. % PPM"
                        format={decimalFormat} width='9rem' cssClass="table-vertical-align" />
                    <Column dataField='concentration' dataType="number"
                        format={decimalFormat} width='8rem' cssClass="table-vertical-align" />
                    <Column dataField='density' dataType="number"
                        format={decimalFormat} width='8rem' cssClass="table-vertical-align" />

                    <Column dataField='cooId' caption="COO" dataType="number" width='10rem'
                        cellRender={cooItemRender} cssClass="table-vertical-align">
                        <Lookup dataSource={countries}
                            allowClearing={true} valueExpr='id' displayExpr="description" />
                    </Column>

                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">{title()}</div>
                            </div>
                        </ToolbarItem>
                        {/*
                        {!props.isReadOnly &&
                            <ToolbarItem location="after">
                                <Button hint="Edit" icon="inserttable"
                                    width="1.8rem" onClick={showMatrixEditor} />
                            </ToolbarItem>
                        }
                        */}
                        <ToolbarItem location='after' name="addRowButton" />

                    </Toolbar>

                    <Editing mode="popup"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={true}
                        allowDeleting={true}>

                        <Popup title="Product Raw Material Batch" showTitle={true} width="80vw" height="80vh" />
                        <Form>
                            <Item dataField="batchName" />
                            <Item dataField="vendorId" />
                            <Item dataField="vendorPartNumber" />
                            <Item dataField="vendorPartName" />
                            <Item dataField="purity" />
                            <Item dataField="stockConcentrationPercentPpm" />
                            <Item dataField="concentration" />
                            <Item dataField="density" />
                            <Item dataField="cooId" />
                        </Form>
                    </Editing>
                </DataGrid>
            </div>

            {selectedRawMaterialBatch &&
                <div className="pw-form-section">
                    <div className="lq-sub-title pw-margin-top-1 pw-margin-bottom-05">Documents</div>

                    <DocumentLoader parentId={selectedRawMaterialBatch && selectedRawMaterialBatch.rawMaterialBatchId}
                        documentType={DocumentType.ProductSds}
                        readOnly={false} canDelete={true} />

                </div>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }
        </div>
    );

    function onRawMaterialBatchSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedRawMaterialBatch(e.selectedRowsData[0]);
    }

    async function onRawMaterialBatchRowValidating(e: any) {
        if (e.oldData) {
            let rawMaterialBatch = e.oldData as RawMaterialBatch;

            if (rawMaterialBatch) {
                let updatedRawMaterialBatch = { ...rawMaterialBatch, ...e.newData };

                updateRawMaterialBatch(updatedRawMaterialBatch)
                    .then(() => {
                        showToast("Raw Material Batch updated", "success");
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Batch");
                    });
            }
        }
        else {
            if (e.newData) {
                let rawMaterialBatch = e.newData as RawMaterialBatch;

                rawMaterialBatch.productId = props.productId;

                await insertRawMaterialBatch(rawMaterialBatch)
                    .then(() => {
                        showToast("Raw Material Batch saved", "success");
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Inserting Batch");
                    });
            }
        }
    }

    async function onRawMaterialBatchRowRemoving(e: any) {
        if (selectedRawMaterialBatch && selectedRawMaterialBatch.rawMaterialBatchId) {
            /*
                deleteGcColumnHeader(selectedGcColumnHeader.columnHeaderId)
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Deleting GC Column Header");
                        });
    
                        */
        }
    }

    async function getRawMaterialBatches() {
        await CrudFunctions.getData(`products/rawmaterialbatches/${props.productId}`)
            .then(data => {
                setRawMaterialBatches(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Raw Material Batches");
            });
    }

    async function getVendors() {
        await CrudFunctions.getData("common/vendors")
            .then(data => {
                setVendors(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Vendors");
            });
    }

    async function getCountries() {
        await CrudFunctions.getData("common/countrycodes")
            .then(data => {
                setCountries(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Country Codes");
            });
    }

    async function insertRawMaterialBatch(batch: RawMaterialBatch) {
        return await CrudFunctions.postData("products/productrawmaterialbatch", batch);
    }

    async function updateRawMaterialBatch(batch: RawMaterialBatch) {
        return await CrudFunctions.putData("products/productrawmaterialbatch", batch);
    }

    async function getAllData() {
        await Promise.all([
            getRawMaterialBatches(),
            getVendors(),
            getCountries()
        ]);
    }

    function cooItemRender(e: any) {
        const batch = e.data;

        if (!batch.cooId)
            return <div></div>;

        const matchingCountry = countries.find(c => c.id === batch.cooId);

        if (matchingCountry) {
            return (
                <div>{`${matchingCountry.description} (${matchingCountry.name})`}</div>
            );
        }

        return <div></div>;
    }

    function onMethodAuditButtonClicked(e: any) {
    }

    function onMethodInformationButtonClicked(e: any) {
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}


